import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Form,
  Row,
  Col,
  Input,
  Switch,
  message,
  Spin,
  Modal
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import WordSelectionContent from '@/components/Modal/WordSelectionContent';
import WordTable from '@/components/organisms/Table/WordTable';
import { adminWordbookApi } from '@/services/api';

const { Title } = Typography;

interface WordbookDetail {
  id: string;
  title: string;
  words: string;
  isPublic: boolean;
  registrant: string;
  registrationDate: string;
}

interface WordData {
  key: string;
  no: number;
  word: string;
  meaning: string;
  ttsType: string;
}

interface SelectedWord {
  id: string;
  word: string;
  meaning: string;
  ttsType: string;
}

const AdminWordbookDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wordbookId } = useParams<{ wordbookId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordbookData, setWordbookData] = useState<WordbookDetail | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [wordList, setWordList] = useState<WordData[]>([]);
  const [wordSelectionModalVisible, setWordSelectionModalVisible] = useState(false);
  type SelectionWordData = {
    id: string;
    wordCode: string;
    word: string;
    meaning: string;
    ttsType: string;
    isPublic: boolean;
    registrant: string;
    createdAt: string;
    uploadStatus: 'completed' | 'failed';
  };

  const [learningHistoryModalVisible, setLearningHistoryModalVisible] = useState(false);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [pendingValues, setPendingValues] = useState<any | null>(null);

  const isEditMode = !!wordbookId;

  const fromPath = (location.state as { from?: string } | null)?.from;

  const handleBackToList = () => {
    navigate(fromPath || '/admin/wordbook');
  };

  const submitWordbook = async (values: any) => {
    try {
      setSaving(true);
      const payload = {
        title: (values?.title || '').trim(),
        isPublic: values?.isPublic ?? true,
        registrant: values?.registrant || '',
        wordIds: wordList.map(w => Number(w.key)).filter(v => !Number.isNaN(v))
      };

      let res: any;
      if (isEditMode) {
        const idNum = Number(wordbookId);
        res = await adminWordbookApi.update(idNum, { title: payload.title, isPublic: payload.isPublic, wordIds: payload.wordIds });
      } else {
        res = await adminWordbookApi.create(payload);
      }
      if (res?.success) {
        setSuccessModalVisible(true);
      } else {
        setErrorModalVisible(true);
      }
    } catch (e) {
      setErrorModalVisible(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (wordList.length === 0) {
      message.error('단어를 선택해주세요.');
      return;
    }

    if (isEditMode && usageCount > 0) {
      setPendingValues(values);
      setLearningHistoryModalVisible(true);
      return;
    }

    await submitWordbook(values);
  };



  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    navigate('/admin/wordbook');
  };

  const handleErrorModalOk = () => {
    setErrorModalVisible(false);
  };

  const handleLearningHistoryConfirm = async () => {
    setLearningHistoryModalVisible(false);
    if (pendingValues) {
      await submitWordbook(pendingValues);
      setPendingValues(null);
    }
  };

  const handleLearningHistoryCancel = () => {
    setLearningHistoryModalVisible(false);
    setPendingValues(null);
  };

  const handleCancel = () => {
    navigate('/admin/wordbook');
  };


  const handleDeleteWord = (key: string) => {
    setWordList(prev => {
      const next = prev.filter(item => item.key !== key);
      const renumbered = next.map((w, i) => ({ ...w, no: i + 1 }));
      const wordText = renumbered.map(w => w.word).join(', ');
      form.setFieldValue('words', wordText);
      return renumbered;
    });
    message.success('단어가 삭제되었습니다.');
  };

  // 단어 선택 모달 열기
  const handleOpenWordSelection = () => {
    setWordSelectionModalVisible(true);
  };

  // 단어 선택 완료
  const handleWordSelectionComplete = (selectedWords: SelectionWordData[]) => {
    setWordList(prev => {
      const existingIds = new Set(prev.map(w => w.key));
      const toAppend = selectedWords.filter(w => !existingIds.has(w.id)).map(w => ({
        key: w.id,
        no: 0, // 임시, 아래에서 재번호
        word: w.word,
        meaning: w.meaning,
        ttsType: w.ttsType
      }));
      const merged = [...prev, ...toAppend];
      const renumbered = merged.map((w, i) => ({ ...w, no: i + 1 }));
      const wordText = renumbered.map(w => w.word).join(', ');
      form.setFieldValue('words', wordText);
      const addedCount = toAppend.length;
      setWordSelectionModalVisible(false);
      if (addedCount > 0) message.success(`${addedCount}개의 단어가 추가되었습니다.`);
      else message.info('추가된 새 단어가 없습니다.');
      return renumbered;
    });
  };

  // 단어 선택 모달 취소
  const handleWordSelectionCancel = () => {
    setWordSelectionModalVisible(false);
  };

  useEffect(() => {
    if (isEditMode) {
      // 수정 모드: 데이터 로드 (실제 API)
      (async () => {
        try {
          setLoading(true);
          const idNum = Number(wordbookId);
          const res: any = await adminWordbookApi.detail(idNum);
          const data = res?.data || res;
          const wbTitle = data?.title || '';
          const isPublicVal = !!data?.isPublic;
          const createdAt = (data?.createdAt || '').split('T')[0] || '';
          const words = (data?.words || []) as Array<{ id: number, word: string, meaning: string, ttsType: string }>;

          const initialList = words.map((w, idx) => ({ key: String(w.id), no: idx + 1, word: w.word, meaning: w.meaning, ttsType: w.ttsType }));
          setWordList(initialList);
          setWordbookData({ id: String(data?.id || ''), title: wbTitle, words: '', isPublic: isPublicVal, registrant: data?.registrant || '-', registrationDate: createdAt });
          form.setFieldsValue({ title: wbTitle, isPublic: isPublicVal, registrant: data?.registrant || '-' });
          form.setFieldValue('words', initialList.map(w => w.word).join(', '));
          setUsageCount((typeof data?.usageCount === 'number') ? data.usageCount : 0);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      // 등록 모드: 빈 폼으로 시작
      form.setFieldsValue({
        isPublic: true,
        registrant: '관리자' // 실제로는 로그인한 사용자 정보
      });
    }
  }, [wordbookId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '단어장 상세' : '단어장 등록'}
          </Title>
          {/* 단어장코드 표시 제거 */}
        </div>

        {/* 단어장 정보 폼 */}
        <Card style={{ marginBottom: 24 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="단어장 제목"
                  name="title"
                  rules={[
                    { required: true, message: '단어장 제목을 입력해주세요' },
                    { min: 1, max: 100, message: '단어장 제목은 1~100자로 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="단어장 제목을 입력하세요"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="등록자"
                  name="registrant"
                >
                  <Input
                    disabled
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
              {isEditMode && wordbookData && (
                  <Form.Item
                    label="등록일"
                  >
                    <Input
                      value={wordbookData.registrationDate}
                      disabled
                      size="large"
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="공개 여부">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Form.Item
                      name="isPublic"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch checkedChildren="공개" unCheckedChildren="비공개" />
                    </Form.Item>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                      공개 시 모든 학원/학생이 확인하며, 비공개 시 다른 사용자에게는 노출되지 않습니다.
                    </span>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Col span={12}>
                <Form.Item
                  label="단어 등록"
                  name="words"
                  rules={[
                    { required: true, message: '단어를 선택해주세요' }
                  ]}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    
                    <Button 
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleOpenWordSelection}
                      size="large"
                    >
                      단어 선택
                    </Button>
                  </div>
                </Form.Item>
            </Col>
          </Form>
        </Card>

        {/* 단어 목록 테이블 */}
        {wordList.length > 0 && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>단어 목록</h3>
            </div>
            <div className='overflow-x-auto max-h-[500px]'>
                <WordTable
              data={wordList.map(word => ({
                id: word.key,
                word: word.word,
                meaning: word.meaning,
                ttsType: word.ttsType
              }))}
              onDelete={handleDeleteWord}
              showActions={true}
            />
            </div>
            
          </Card>
        )}

        {/* 버튼 영역 */}
        <div style={{ textAlign: 'right', marginTop: 32 }}>
              <Space size="middle">
                <Button 
                  onClick={handleCancel}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  취소
                </Button>
                <Button 
                  type="primary" 
                  onClick={() => form.submit()}
                  loading={saving}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </Button>
              </Space>
            </div>

        {/* 단어 선택 모달 */}
        <Modal
          open={wordSelectionModalVisible}
          onCancel={handleWordSelectionCancel}
          footer={null}
          width={900}
          destroyOnClose
        >
          <WordSelectionContent
            onOk={handleWordSelectionComplete}
            onCancel={handleWordSelectionCancel}
            title="단어 선택"
            useAdminApi
          />
        </Modal>

        {/* 성공 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
              <span>저장 완료</span>
            </div>
          }
          open={successModalVisible}
          onOk={handleSuccessModalOk}
          onCancel={handleSuccessModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleSuccessModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              단어장이 성공적으로 {isEditMode ? '수정' : '등록'}되었습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              {isEditMode ? '수정된 단어장은 단어장 목록에서 확인할 수 있습니다.' : '등록된 단어장은 단어장 목록에서 확인할 수 있습니다.'}
            </div>
          </div>
        </Modal>

        {/* 학습이력 확인 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>학습이력 확인</span>
            </div>
          }
          open={learningHistoryModalVisible}
          onOk={handleLearningHistoryConfirm}
          onCancel={handleLearningHistoryCancel}
          footer={[
            <Button key="cancel" onClick={handleLearningHistoryCancel}>
              취소
            </Button>,
            <Button key="confirm" type="primary" onClick={handleLearningHistoryConfirm}>
              수정하기
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold', color: '#faad14' }}>
              학습이력이 있는 단어장입니다.
            </div>
            <div style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              이 단어장은 학생들이 학습한 기록이 있습니다.<br />
              수정하시겠습니까?
            </div>
            <div style={{ 
              backgroundColor: '#fff7e6', 
              border: '1px solid #ffd591', 
              borderRadius: '6px', 
              padding: '12px',
              fontSize: '13px',
              color: '#d46b08'
            }}>
              ⚠️ 수정 시 기존 학습 데이터에 영향을 줄 수 있습니다.
            </div>
          </div>
        </Modal>

        {/* 실패 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
              <span>저장 실패</span>
            </div>
          }
          open={errorModalVisible}
          onOk={handleErrorModalOk}
          onCancel={handleErrorModalOk}
          footer={[
            <Button key="ok" type="primary" danger onClick={handleErrorModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold', color: '#ff4d4f' }}>
              단어장 {isEditMode ? '수정' : '등록'}에 실패했습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              다시 시도해주세요.
            </div>
          </div>
        </Modal>

        {/* 진행 상태 모달 */}
        <Modal
          open={saving}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={420}
        >
          <div style={{ textAlign: 'center', padding: '24px 12px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>
              단어장을 저장하는 중입니다
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>
              단어 수가 많을수록 최대 1~2분까지 소요될 수 있습니다. 창을 닫지 말고 잠시만 기다려주세요.
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminWordbookDetail; 